import { useCallback, useEffect, useState } from 'react';
import { api, type SignalIntegrityState } from '../api/client';
import { useToast } from '../hooks/useToast';
import ToastStack from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';

/**
 * SRS 6.2.3 and 6.2.4, on one page deliberately.
 *
 * The switches alone would be a loaded gun with no instructions: an operator
 * facing a suspected fraud wave at 2am should not have to remember which of
 * the two to pull. So the conditions that justify a suspension sit directly
 * above the control that applies it, each naming the measure it calls for.
 * The long form lives in docs/SIGNAL_LIVE_GOVERNANCE.md; this is the version
 * you can act on without leaving the screen.
 */

interface Condition {
  title: string;
  observed: string;
  measure: string;
  /** Which switches the condition calls for — shown as a tag, not applied. */
  applies: ('publication' | 'feed')[];
}

const CONDITIONS: Condition[] = [
  {
    title: 'Expirations non fiables',
    observed:
      'Une offre reste visible après son expiration, ou disparaît trop tôt. Le nombre d’offres LIVE ne bouge plus.',
    measure:
      'L’expiration est le signal. Une offre qui survit à son compte à rebours prouve que le compte à rebours ment.',
    applies: ['feed'],
  },
  {
    title: 'Incohérence d’horloge',
    observed:
      'Le temps restant affiché ne correspond plus au /time du serveur, ou deux tâches renvoient des heures différentes.',
    measure:
      'Prix, paliers, expiration et fenêtre d’arrivée sortent tous de cette horloge. Rien n’est fiable tant qu’elle ne l’est pas.',
    applies: ['feed', 'publication'],
  },
  {
    title: 'Abus de pricing',
    observed:
      'Un prix qui ne correspond à aucun palier configuré, une remise moyenne aberrante, ou des changements de palier absents du journal.',
    measure:
      'Un prix que le système ne sait pas justifier par le temps restant ne doit pas pouvoir être payé.',
    applies: ['feed'],
  },
  {
    title: 'Offres non réelles',
    observed:
      'Logements inexistants signalés, preuves de cession falsifiées, plusieurs offres d’un même hôte à des adresses invraisemblables.',
    measure:
      'Retirer offre par offre tant que les cas sont identifiables. Suspendre la publication si le rythme dépasse le retrait à la main — le feed reste ouvert, les offres légitimes ne sont pas en cause.',
    applies: ['publication'],
  },
  {
    title: 'Défaillance du verrou de réservation',
    observed:
      'Une double réservation, un pic de lock_timeout dans les journaux, ou Redis indisponible.',
    measure:
      'Une offre réservée deux fois envoie un voyageur devant une porte occupée. Le signal disait « disponible ».',
    applies: ['feed'],
  },
];

function formatChangedAt(iso: string | null): string {
  if (!iso) return 'jamais modifié';
  return new Date(iso).toLocaleString('fr-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export default function SignalIntegrity() {
  const { toasts, push, dismiss } = useToast();
  const [state, setState] = useState<SignalIntegrityState | null>(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<{
    publication: boolean;
    feed: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await api.getSignalIntegrity();
      setState(result.data);
    } catch (err) {
      push(
        err instanceof Error ? err.message : 'Une erreur est survenue.',
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [push]);

  useEffect(() => {
    void load();
  }, [load]);

  const suspended =
    state != null && (state.publication_suspended || state.feed_suspended);

  // Restoring needs no reason — nominal is the default state — but suspending
  // does, and the button says so rather than failing at the server.
  const requestChange = (next: { publication: boolean; feed: boolean }) => {
    setPending(next);
  };

  async function apply() {
    if (!pending) return;
    setBusy(true);
    try {
      const result = await api.setSignalIntegrity({
        publication_suspended: pending.publication,
        feed_suspended: pending.feed,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setState(result.data);
      setReason('');
      push(
        pending.publication || pending.feed
          ? 'Suspension appliquée.'
          : 'Fonctionnement nominal rétabli.',
        'success',
      );
      setPending(null);
    } catch (err) {
      push(
        err instanceof Error ? err.message : 'Une erreur est survenue.',
        'error',
      );
    } finally {
      setBusy(false);
    }
  }

  const willSuspend = pending != null && (pending.publication || pending.feed);
  const reasonMissing = willSuspend && reason.trim().length < 3;

  return (
    <>
      <ToastStack toasts={toasts} dismiss={dismiss} />

      <div className="section-head">
        <div>
          <div className="section-title">État actuel</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            En cas de doute, suspendre d’abord et enquêter ensuite. Suspendre
            est réversible et ne touche à aucun paiement déjà confirmé.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 20 }}>
          <span className="spinner" /> Chargement…
        </div>
      ) : (
        <>
          <div
            className={`card signal-state${suspended ? ' signal-state-alert' : ''}`}
          >
            <div className="signal-state-head">
              <span className="signal-state-dot" aria-hidden="true" />
              <strong>
                {suspended
                  ? 'Signal suspendu'
                  : 'Fonctionnement nominal'}
              </strong>
            </div>
            <div className="signal-state-lines">
              <div>
                Publication :{' '}
                <strong>
                  {state?.publication_suspended ? 'suspendue' : 'ouverte'}
                </strong>
              </div>
              <div>
                Feed :{' '}
                <strong>{state?.feed_suspended ? 'suspendu' : 'ouvert'}</strong>
              </div>
              {state?.reason && <div>Motif : « {state.reason} »</div>}
              <div className="signal-state-meta">
                Dernier changement : {formatChangedAt(state?.changed_at ?? null)}
                {state?.actor ? ` · ${state.actor}` : ''}
              </div>
            </div>
          </div>

          <div className="signal-actions">
            <div className="field">
              <label htmlFor="signal-reason">
                Motif {willSuspend ? '(obligatoire pour suspendre)' : '(facultatif)'}
              </label>
              <input
                id="signal-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex. : offres non réelles signalées par trois voyageurs"
                autoComplete="off"
                disabled={busy}
              />
            </div>

            <div className="signal-buttons">
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy || state?.publication_suspended === true}
                onClick={() =>
                  requestChange({
                    publication: true,
                    feed: state?.feed_suspended ?? false,
                  })
                }
              >
                Suspendre la publication
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy || state?.feed_suspended === true}
                onClick={() =>
                  requestChange({
                    publication: state?.publication_suspended ?? false,
                    feed: true,
                  })
                }
              >
                Suspendre le feed
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy || !suspended}
                onClick={() => requestChange({ publication: false, feed: false })}
              >
                Tout rétablir
              </button>
            </div>

            <div className="signal-note">
              Une suspension met jusqu’à 5 secondes à s’appliquer sur toutes les
              instances du serveur. Chaque changement est journalisé avec son
              auteur et son motif.
            </div>
          </div>

          <hr className="divider" />

          <div className="section-head">
            <div>
              <div className="section-title">
                Quand suspendre — les cinq conditions
              </div>
              <div
                style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}
              >
                Si l’une de ces situations est constatée, appliquer la mesure
                sans attendre d’en comprendre la cause. Le diagnostic vient
                après.
              </div>
            </div>
          </div>

          <div className="signal-conditions">
            {CONDITIONS.map((condition) => (
              <div key={condition.title} className="card signal-condition">
                <div className="signal-condition-head">
                  <strong>{condition.title}</strong>
                  <span className="signal-condition-tags">
                    {condition.applies.includes('feed') && (
                      <span className="signal-tag">feed</span>
                    )}
                    {condition.applies.includes('publication') && (
                      <span className="signal-tag">publication</span>
                    )}
                  </span>
                </div>
                <div className="signal-condition-observed">
                  {condition.observed}
                </div>
                <div className="signal-condition-measure">
                  {condition.measure}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {pending && (
        <ConfirmModal
          title={
            willSuspend
              ? 'Confirmer la suspension'
              : 'Rétablir le fonctionnement nominal'
          }
          description={
            willSuspend
              ? `${
                  pending.feed && pending.publication
                    ? 'Le feed et la publication seront suspendus'
                    : pending.feed
                      ? 'Le feed renverra zéro offre et l’app affichera un message explicite'
                      : 'Les hôtes ne pourront plus publier ; leurs brouillons sont conservés'
                }. Aucune réservation confirmée n’est annulée, aucun versement n’est interrompu.${
                  reasonMissing
                    ? ' Renseignez un motif avant de confirmer.'
                    : ''
                }`
              : 'Le feed et la publication redeviennent disponibles immédiatement.'
          }
          confirmLabel={willSuspend ? 'Suspendre' : 'Rétablir'}
          confirmVariant={willSuspend ? 'danger' : 'primary'}
          onConfirm={() => {
            if (reasonMissing) return;
            void apply();
          }}
          onCancel={() => setPending(null)}
          loading={busy}
        />
      )}
    </>
  );
}
